import { useDispatch } from "react-redux";

import { apiThatThrows } from "containers/Request";
import { RELEASE_STAGES, TiVoPublications, TiVoTargets } from "hooks/tivoApps";
import { transformVariantPublications } from "hooks/tivoApps/transformers";
import {
  getOems,
  TiVoDeviceClientType,
  TiVoDeviceOem,
  TiVoDevicePlatform,
} from "hooks/tivoDevices";
import { ApiQueryParams } from "types/api";

function exportCsv(
  fileName: string,
  header: string,
  body: string[],
  comments: string[] = []
) {
  const csv = [header, ...body, ...comments].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

export function exportPublications(
  publications: TiVoPublications[],
  platform: TiVoDevicePlatform,
  targets: TiVoTargets,
  filters: string,
  fileName: string
) {
  const cols = getOems([platform], targets).map((oem) => {
    if (!platform.targetedByClientType) {
      return oem;
    }
    let clientTypes = oem.clientTypes;
    if (!clientTypes || clientTypes.length === 0) {
      return oem;
    }
    if (
      clientTypes.some((clientType) =>
        targets.clientTypes?.includes(clientType.id)
      )
    ) {
      clientTypes = clientTypes.filter((clientType) =>
        targets.clientTypes?.includes(clientType.id)
      );
    }
    return [oem, clientTypes] as [TiVoDeviceOem, TiVoDeviceClientType[]];
  });
  const header = ["Provider", "Variant"]
    .concat(
      cols
        .map((col) => {
          if (!Array.isArray(col)) {
            return col.name;
          }
          const oem = col[0];
          return col[1].map((clientType) => `${oem.name} > ${clientType.name}`);
        })
        .flat()
        .map((col) => {
          return RELEASE_STAGES.map((rs) => `${col} > ${rs.toUpperCase()}`);
        })
        .flat()
    )
    .join(",");
  const body = publications.map((pub) => {
    const row = [
      `"${pub.variant?.provider?.label || ""}"`,
      `"${pub.variant?.label || ""}"`,
    ];
    for (const col of cols) {
      const [oem, clientTypes] = Array.isArray(col) ? col : [col, [null]];
      const oemPub =
        pub.platforms.find(({ platformId }) => platformId === platform.id)
          ?.publications?.[oem.id] || {};
      /**
       * We only want to export the published (true) or unpublished (false)
       * status. We do not need to check if an OEM or a client type is targeted,
       * because we want to export the back end state as is and we do not need
       * to know why the OEM or the client type is unpublished.
       */
      for (const clientType of clientTypes) {
        for (const rs of RELEASE_STAGES) {
          const rsPub = oemPub[rs];
          if (!rsPub) {
            row.push("false");
            continue;
          }
          if (clientType) {
            row.push(
              rsPub.length === 0 || clientType.id in rsPub ? "true" : "false"
            );
          } else {
            row.push("true");
          }
        }
      }
    }
    return row.join(",");
  });
  const comments = [
    `"# Timestamp: ${new Date().toISOString()}"`,
    `"# Platform: ${platform.name}"`,
    `"# OEMs: ${cols.map((col) => (Array.isArray(col) ? col[0] : col).name).join(", ")}"`,
    `"# Filters: ${filters.replaceAll('"', "'") || "none"}"`,
  ];
  exportCsv(fileName, header, body, comments);
}

export async function fetchPublications(
  dispatch: ReturnType<typeof useDispatch>,
  platformId: string,
  queryParams: ApiQueryParams,
  concurrency: number
) {
  const limit = Number(queryParams.limit);
  const publications: TiVoPublications[] = [];
  if (!Number.isInteger(concurrency) || concurrency < 1) {
    throw new Error(`the concurrency argument is invalid: ${concurrency}`);
  }
  if (!Number.isInteger(limit) || limit < 1) {
    throw new Error(`the limit argument is invalid: ${limit}`);
  }
  let lastPageIdx = 1;
  let lastPages: TiVoPublications[][] = [];
  while (lastPages.every((page) => page.length === limit)) {
    const pageIdxs = [...Array(concurrency).keys()].map(
      // eslint-disable-next-line no-loop-func
      (idx) => lastPageIdx + idx
    );
    lastPageIdx += concurrency;
    lastPages = await Promise.all(
      pageIdxs.map(async (pageIdx) => {
        // See usePublications().
        const { results } = await dispatch(
          apiThatThrows.getTiVoPublications.action({
            params: { platformId },
            queryParams: {
              ...queryParams,
              page: pageIdx,
            },
          })
        );
        return transformVariantPublications(results, platformId);
      })
    );
    publications.push(...lastPages.flat());
  }
  return publications;
}
